import React, { createContext, useContext, useState, useEffect } from 'react';
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';

const AuthContext = createContext();

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-1_eQ4oSjtGM',
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '7123hmqco0fqs1j2hfd0kau4hs'
};

const userPool = new CognitoUserPool(poolData);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err, session) => {
        if (err) {
          setLoading(false);
          return;
        }
        
        if (session.isValid()) {
          const payload = session.getIdToken().payload;
          const adminFlag = payload['custom:admin'];
          
          if (adminFlag === 'true') {
            setUser(currentUser);
            setIsAdmin(true);
          } else {
            // Not an admin, sign out
            currentUser.signOut();
          }
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      user.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const payload = session.getIdToken().payload;
          const adminFlag = payload['custom:admin'];
          
          if (adminFlag === 'true') {
            setUser(user);
            setIsAdmin(true);
            resolve(user);
          } else {
            user.signOut();
            reject(new Error('Admin access required'));
          }
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  };

  const signOut = () => {
    if (user) {
      user.signOut();
    }
    setUser(null);
    setIsAdmin(false);
  };

  const getAuthToken = () => {
    return new Promise((resolve, reject) => {
      if (!user) {
        reject(new Error('No authenticated user'));
        return;
      }

      user.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }
        
        if (session.isValid()) {
          resolve(session.getIdToken().getJwtToken());
        } else {
          reject(new Error('Session invalid'));
        }
      });
    });
  };

  const value = {
    user,
    isAdmin,
    loading,
    signIn,
    signOut,
    getAuthToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};